import { wdb_get_new, createAxiosInstance } from '@/plugins/axios.js'
import { bulkPdfError, bulkPdfErrorCode } from '@/modules/cohort/bulkPdfErrors.js'

export const BULK_OAHI_TIMEOUT_MS = 15000
const OAHI_CONFIG_URL = '/wdb/oahi列表/默认列表/?history=60'

// Bulk capture must never turn an unavailable/malformed configuration into AHI.
// Do not reuse the legacy cache: it may contain a silent fallback. One bounded
// canonical request, no retries, no fallback and no changes to non-bulk callers.
export async function getBulkOAHIDevices(device, { signal } = {}) {
  try {
    const deviceId = String(device ?? '')
    if (!deviceId || deviceId.trim() !== deviceId) throw bulkPdfError('config')
    const response = await createAxiosInstance('数据服务器').get(OAHI_CONFIG_URL, {
      params: { device: deviceId }, timeout: BULK_OAHI_TIMEOUT_MS, signal
    })
    const devices = response.data?.data?.devices
    if (!Array.isArray(devices) || devices.some(id => typeof id !== 'string' || id !== deviceId)) {
      throw bulkPdfError('config')
    }
    return devices
  } catch (error) {
    throw bulkPdfError(bulkPdfErrorCode(error, 'config'))
  }
}

// 缓存设备配置，避免频繁请求
let cachedOAHIDevices = null
let cacheExpiry = null
const CACHE_DURATION = 5 * 60 * 1000 // 5分钟缓存

/**
 * 获取 OAHI 设备列表
 * @param {boolean} forceRefresh - 是否强制刷新缓存
 * @returns {Promise<Array<string>>} 设备ID列表
 */
export async function getOAHIDevices(forceRefresh = false) {
  try {
    // 检查缓存是否有效
    const now = Date.now()
    if (!forceRefresh && cachedOAHIDevices && cacheExpiry && now < cacheExpiry) {
      console.log('deviceConfig - 使用缓存的设备列表:', cachedOAHIDevices)
      return cachedOAHIDevices
    }

    console.log('deviceConfig - 开始从数据库获取OAHI设备列表...')
    
    // 使用正确的数据库路径获取 OAHI 设备列表
    // 尝试带history参数的访问方式
    let response = null
    
    try {
      // 方法1：带history参数（参考您提供的链接）
      const url = OAHI_CONFIG_URL
      const axiosInstance = createAxiosInstance('数据服务器')
      const historyResponse = await axiosInstance.get(url)
      response = historyResponse.data
      console.log('deviceConfig - 带history参数的响应:', response)
    } catch (error) {
      console.log('deviceConfig - 带history参数访问失败:', error)
      
      // 方法2：不带history参数
      try {
        response = await wdb_get_new('数据服务器', 'oahi列表', '默认列表')
        console.log('deviceConfig - 不带history参数的响应:', response)
      } catch (error2) {
        console.log('deviceConfig - 不带history参数也失败:', error2)
      }
    }
    
    console.log('deviceConfig - 最终数据库响应:', response)
    
    let deviceList = []
    let isFromDatabase = false
    
    // 提取 devices 数组
    if (response && response.data && Array.isArray(response.data.devices)) {
      deviceList = response.data.devices
      isFromDatabase = true
      console.log('deviceConfig - 从数据库获取的设备列表:', deviceList)
      
      // 检查是否包含时间戳等元数据，以确认是数据库数据
      if (response.data.refresh_time || response.data.time_diff !== undefined) {
        console.log('deviceConfig - 确认为数据库数据，包含元数据:')
        console.log('  refresh_time:', response.data.refresh_time)
        console.log('  time_diff:', response.data.time_diff)
        console.log('  modified:', response.data.modified)
      }
    } else {
      console.error('deviceConfig - 数据库响应格式不正确:', response)
    }
    
    // 如果获取失败，使用空的默认列表（只有数据库中的设备才应该是OAHI设备）
    if (!deviceList || deviceList.length === 0) {
      console.warn('deviceConfig - 无法获取OAHI设备配置，使用空的默认列表')
      console.warn('deviceConfig - 只有数据库中明确配置的设备才会被识别为OAHI设备')
      deviceList = [] // 空列表，避免错误识别设备
      isFromDatabase = false
    }
    
    // 更新缓存
    cachedOAHIDevices = deviceList
    cacheExpiry = now + CACHE_DURATION
    
    console.log('deviceConfig - 最终使用的OAHI设备列表:', deviceList)
    console.log('deviceConfig - 数据来源:', isFromDatabase ? '数据库' : '硬编码默认值')
    
    return deviceList
  } catch (error) {
    console.error('deviceConfig - 获取OAHI设备列表失败:', error)
    
    // 如果有缓存，返回缓存数据
    if (cachedOAHIDevices) {
      console.warn('deviceConfig - 使用缓存的OAHI设备列表')
      return cachedOAHIDevices
    }
    
    // 最后的兜底方案：返回空列表（避免错误识别设备）
    console.warn('deviceConfig - 数据库访问失败，使用空的OAHI设备列表作为兜底')
    console.warn('deviceConfig - 只有数据库中明确配置的设备才会被识别为OAHI设备')
    const fallbackList = []
    
    // 即使是兜底方案也要缓存
    cachedOAHIDevices = fallbackList
    cacheExpiry = now + CACHE_DURATION
    
    return fallbackList
  }
}

/**
 * 检查设备是否为OAHI设备
 * @param {string} deviceId - 设备ID
 * @returns {Promise<boolean>}
 */
export async function isOAHIDevice(deviceId) {
  if (!deviceId) {
    console.log('deviceConfig - 设备ID为空')
    return false
  }
  
  try {
    const oahiDevices = await getOAHIDevices()
    
    // 确保 deviceId 是字符串类型
    const deviceIdStr = String(deviceId)
    
    // 添加详细的调试信息
    console.log('deviceConfig - 检查设备详情:')
    console.log('  原始设备ID:', deviceId, '类型:', typeof deviceId)
    console.log('  转换后设备ID:', deviceIdStr, '类型:', typeof deviceIdStr)
    console.log('  OAHI设备列表:', oahiDevices)
    console.log('  列表中设备类型:', oahiDevices.map(id => typeof id))
    
    // 检查是否在列表中
    const result = oahiDevices.includes(deviceIdStr)
    
    // 如果不在列表中，逐个比较看看哪里不一样
    if (!result) {
      console.log('deviceConfig - 设备不在列表中，详细比较:')
      oahiDevices.forEach((id, index) => {
        const match = id === deviceIdStr
        console.log(`  [${index}] "${id}" === "${deviceIdStr}" ? ${match}`)
        if (!match && id.trim() === deviceIdStr.trim()) {
          console.log(`    注意：去除空格后匹配！`)
        }
      })
    }
    
    console.log('deviceConfig - 最终结果:', result)
    return result
  } catch (error) {
    console.error('deviceConfig - 检查OAHI设备失败:', error)
    return false
  }
}

/**
 * 清除设备配置缓存
 */
export function clearDeviceConfigCache() {
  console.log('deviceConfig - 手动清除缓存')
  cachedOAHIDevices = null
  cacheExpiry = null
}

/**
 * 获取当前缓存状态（调试用）
 */
export function getDeviceConfigDebugInfo() {
  return {
    cachedDevices: cachedOAHIDevices,
    cacheExpiry: cacheExpiry,
    cacheValid: cacheExpiry && Date.now() < cacheExpiry,
    timeToExpiry: cacheExpiry ? Math.max(0, cacheExpiry - Date.now()) : 0
  }
}

// 将调试函数暴露到全局（仅开发环境）
if (process.env.NODE_ENV === 'development') {
  window.debugOAHI = {
    clearCache: clearDeviceConfigCache,
    getDebugInfo: getDeviceConfigDebugInfo,
    forceRefresh: () => getOAHIDevices(true),
    checkDevice: isOAHIDevice
  }
}

/**
 * 添加OAHI设备（需要后端支持数据库写入）
 * 注意：当前使用只读数据库访问，此功能可能需要后端API支持
 * @param {string} deviceId - 设备ID
 * @returns {Promise<boolean>}
 */
export async function addOAHIDevice(deviceId) {
  console.warn('添加OAHI设备功能需要后端支持，请联系后端开发者实现数据库写入功能')
  
  // 清除缓存，以便下次获取时重新从数据库拉取
  clearDeviceConfigCache()
  
  // 暂时返回 false，表示前端无法直接添加
  return false
}

/**
 * 移除OAHI设备（需要后端支持数据库写入）
 * 注意：当前使用只读数据库访问，此功能可能需要后端API支持
 * @param {string} deviceId - 设备ID
 * @returns {Promise<boolean>}
 */
export async function removeOAHIDevice(deviceId) {
  console.warn('移除OAHI设备功能需要后端支持，请联系后端开发者实现数据库写入功能')
  
  // 清除缓存
  clearDeviceConfigCache()
  
  // 暂时返回 false，表示前端无法直接移除
  return false
} 

// 开发环境下立即清除缓存，确保使用最新数据
if (process.env.NODE_ENV === 'development') {
  console.log('deviceConfig - 开发环境，清除OAHI设备缓存')
  clearDeviceConfigCache()
} 